/**
 * CustomerDetailDialog Component
 *
 * @description 顧客詳細ダイアログ - 世界最高水準UI実装
 *
 * Features:
 * - Container Queries活用（@md:grid-cols-2）によるレスポンシブグリッド
 * - ScrollArea活用（max-h-[80vh]）による大量情報の表示
 * - Framer Motionによるアニメーション付き表示/非表示
 * - 2カラムレイアウトによる情報の効率的な表示
 * - WCAG 2.1 AA準拠のアクセシビリティ
 * - タッチターゲット最小44x44px（モバイル対応）
 * - キーボードナビゲーション完全対応
 */

import React, { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

import type { CustomerDetailDialogProps } from '@/types/dashboard';

/**
 * 情報カードコンポーネント
 */
const InfoCard: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-medium text-muted-foreground mb-2">{title}</h3>
    <Card className="p-4">
      <div className="space-y-2">{children}</div>
    </Card>
  </div>
);

/**
 * 情報行コンポーネント
 */
const InfoRow: React.FC<{
  label: string;
  value: string | number;
  bold?: boolean;
}> = ({ label, value, bold = false }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`text-sm ${bold ? 'font-medium' : ''}`}>{value}</span>
  </div>
);

/**
 * CustomerDetailDialog Component
 */
const CustomerDetailDialog: React.FC<CustomerDetailDialogProps> = ({
  open,
  onOpenChange,
  customer,
  onNavigateToDetail,
}) => {
  // イベントハンドラー（useCallbackで最適化）
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleNavigate = useCallback(() => {
    if (customer) {
      onNavigateToDetail(customer.id);
      handleClose();
    }
  }, [customer, onNavigateToDetail, handleClose]);

  // 顧客データがない場合は何も表示しない
  if (!customer) {
    return null;
  }

  // 顧客名のイニシャル
  const initial = customer.name.charAt(0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 overflow-hidden">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col h-full"
            >
              {/* ヘッダー */}
              <DialogHeader className="p-6 pb-4">
                <div className="flex items-center gap-4">
                  <Avatar className="w-14 h-14 flex-shrink-0">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                      {initial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <DialogTitle className="text-2xl font-semibold truncate">
                      {customer.name}
                    </DialogTitle>
                    <DialogDescription className="text-sm truncate">
                      {customer.kana}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              {/* コンテンツ（ScrollArea with max-h-[80vh]） */}
              <ScrollArea className="flex-1 px-6">
                <div className="@container pb-6">
                  <div className="grid gap-6 grid-cols-1 @md:grid-cols-2">
                    {/* 基本情報 */}
                    <InfoCard title="基本情報">
                      <InfoRow label="電話番号" value={customer.phone} />
                      <InfoRow label="メール" value={customer.email} />
                      <InfoRow label="生年月日" value={customer.birthDate} />
                    </InfoCard>

                    {/* 利用状況 */}
                    <InfoCard title="利用状況">
                      <InfoRow
                        label="来店回数"
                        value={`${customer.visitCount}回`}
                        bold
                      />
                      <InfoRow
                        label="累計利用金額"
                        value={`¥${customer.totalSpent.toLocaleString()}`}
                        bold
                      />
                      <InfoRow label="最終来店" value={customer.lastVisit} />
                    </InfoCard>

                    {/* 施術選好 */}
                    <InfoCard title="施術選好">
                      <InfoRow label="圧力" value={customer.preferences.pressure} />
                      <InfoRow label="アロマ" value={customer.preferences.aroma} />
                      <InfoRow label="部屋" value={customer.preferences.room} />
                    </InfoCard>

                    {/* スタッフメモ */}
                    <InfoCard title="スタッフメモ">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {customer.notes}
                      </p>
                    </InfoCard>

                    {/* タグ - 全幅 */}
                    <div className="col-span-full">
                      <InfoCard title="タグ">
                        <div className="flex gap-2 flex-wrap">
                          {customer.tags.map((tag, index) => (
                            <Badge
                              key={index}
                              variant={tag.includes('回目') ? 'default' : 'secondary'}
                              className="whitespace-nowrap"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </InfoCard>
                    </div>
                  </div>
                </div>
              </ScrollArea>

              {/* フッター */}
              <DialogFooter className="p-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="min-h-[44px]"
                >
                  閉じる
                </Button>
                <Button onClick={handleNavigate} className="min-h-[44px]">
                  詳細画面へ
                </Button>
              </DialogFooter>
            </motion.div>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default CustomerDetailDialog;
